import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required')
}

declare global {
  interface Window {
    Clerk?: {
      session?: {
        getToken: () => Promise<string | null>
      }
    }
  }
}

export interface CreateSupabaseClientOptions<SchemaName extends string = 'public'> {
  /** Postgres/PostgREST schema this client is scoped to. Defaults to `'public'`. */
  schema?: SchemaName
}

/**
 * Creates a Supabase client wired for Clerk's native Third-Party Auth provider (PR #413
 * review-hardening, F3 — factored out of the module-scope `supabase` export below so
 * schema-scoped consumers don't have to hand-fork this wiring; see
 * `platform/meetings/src/lib/supabase.ts`, whose tables live under the `meetings` schema
 * rather than `public`). `getToken()` is called without a template — Supabase validates
 * the raw Clerk session JWT directly. Per-app RLS (is_admin() / is_l3_team() / …) gates
 * rows from there.
 */
export function createSupabaseClient<SchemaName extends string = 'public'>(
  options: CreateSupabaseClientOptions<SchemaName> = {},
) {
  return createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    ...(options.schema ? { db: { schema: options.schema } } : {}),
    global: {
      fetch: async (input, init) => {
        const token =
          typeof window !== 'undefined' && window.Clerk?.session
            ? await window.Clerk.session.getToken()
            : null
        const headers = new Headers(init?.headers)
        if (token) headers.set('Authorization', `Bearer ${token}`)
        return fetch(input, { ...init, headers })
      },
    },
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  })
}

// Supabase is configured with Clerk's native Third-Party Auth provider, so
// getToken() is called without a template — Supabase validates the raw Clerk
// session JWT directly. Per-app RLS (is_admin() / is_l3_team() / …) gates rows.
export const supabase: SupabaseClient = createSupabaseClient()

export type { SupabaseClient }
